import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { getUser, logout } from '../../services/helpers'
import { getWalletBalance, getWalletTransactions } from '../../services/wallet'
import {
  ChartBarIcon,
  CalendarIcon,
  PlusCircleIcon,
  TagIcon,
  ShoppingBagIcon,
  TicketIcon,
  WalletIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  BuildingOfficeIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  BanknotesIcon,
  DocumentArrowDownIcon,
  ExclamationTriangleIcon,
  PhotoIcon
} from '@heroicons/react/24/outline'

const CenterWalletPage = () => {
  const navigate = useNavigate()
  const [user, setUser] = useState(getUser())
  const displayName = user?.organization || user?.name || 'Event Center'
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [balance, setBalance] = useState(0)
  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all') // all, credit, debit

  const tabs = [
    { id: 'overview', label: 'Overview', icon: ChartBarIcon, path: '/center' },
    { id: 'events', label: 'My Events', icon: CalendarIcon, path: '/center/events', disabled: true },
    { id: 'offers', label: 'My Offers', icon: TagIcon, path: '/center/offers' },
    { id: 'orders', label: 'Orders', icon: ShoppingBagIcon, path: '/center/orders' },
    { id: 'tickets', label: 'Tickets', icon: TicketIcon, path: '/center/tickets' },
    { id: 'media', label: 'Media Gallery', icon: PhotoIcon, path: '/center/media', disabled: true },
    { id: 'disputes', label: 'Disputes', icon: ExclamationTriangleIcon, path: '/center/disputes' },
    { id: 'wallet', label: 'Wallet', icon: WalletIcon, path: '/center/wallet' },
    { id: 'profile', label: 'Profile', icon: UserCircleIcon, path: '/center' }
  ]

  useEffect(() => {
    fetchWalletData()
  }, [])

  const fetchWalletData = async () => {
    setLoading(true)
    const balanceRes = await getWalletBalance()
    if (balanceRes.status === 200) {
      setBalance(balanceRes.data?.balance || 0)
    }

    const transRes = await getWalletTransactions()
    if (transRes.status === 200) {
      // Handle both array and object responses
      const transData = transRes.data
      if (Array.isArray(transData)) {
        setTransactions(transData)
      } else if (transData && transData.transactions && Array.isArray(transData.transactions)) {
        // Backend returns { transactions: [...], wallet: {...} }
        setTransactions(transData.transactions)
      } else {
        console.warn('Unexpected transaction data format:', transData)
        setTransactions([])
      }
    } else {
      console.error('Failed to fetch transactions:', transRes.error)
      setTransactions([])
    }
    setLoading(false)
  }

  const filteredTransactions = Array.isArray(transactions) ? transactions.filter(tx => {
    if (filter === 'all') return true
    if (filter === 'credit') return ['credit', 'deposit'].includes(tx.type)
    if (filter === 'debit') return ['debit', 'withdrawal', 'payout'].includes(tx.type)
    return true
  }) : []

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      {/* Sidebar Navigation */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 h-16 border-b border-slate-100">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-500 flex items-center justify-center text-white shadow-sm">
              <BuildingOfficeIcon className="w-5 h-5" />
            </div>
            <span className="text-xl font-bold text-slate-900 tracking-tight">Obule Panel</span>
            <button onClick={() => setMobileMenuOpen(false)} className="ml-auto lg:hidden">
              <XMarkIcon className="w-6 h-6 text-slate-400" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            {tabs.filter(tab => !tab.disabled).map((tab) => {
              const Icon = tab.icon
              const isActive = tab.id === 'wallet'

              return (
                <Link
                  key={tab.id}
                  to={tab.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-600' : 'text-slate-400'}`} />
                  {tab.label}
                </Link>
              )
            })}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                <UserCircleIcon className="w-6 h-6" />
              </div>
              <div className="overflow-hidden">
                <p className="text-sm font-semibold text-slate-900 truncate">{displayName}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={() => {
                logout()
                navigate('/center/login')
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 lg:ml-64 min-w-0 flex flex-col">
        {/* Top Mobile Header */}
        <header className="lg:hidden bg-white border-b border-slate-200 flex items-center justify-between px-4 h-16 sticky top-0 z-40">
          <button onClick={() => setMobileMenuOpen(true)}>
            <Bars3Icon className="w-6 h-6 text-slate-600" />
          </button>
          <span className="font-semibold text-slate-900">Wallet</span>
          <div className="w-6" />
        </header>

        {/* Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900">Wallet</h1>
            <p className="text-slate-600 mt-1">Manage your earnings and transactions</p>
          </div>

          {/* Balance Card */}
          <div className="mb-8">
            <div className="p-8 rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 p-12 opacity-10 transform translate-x-10 -translate-y-10">
                <WalletIcon className="w-64 h-64" />
              </div>
              <div className="relative z-10">
                <p className="text-slate-400 font-medium mb-1">Total Balance</p>
                <h2 className="text-5xl font-bold mb-8">₦ {balance.toLocaleString()}</h2>
                <div className="flex gap-4">
                  <button className="px-6 py-3 bg-emerald-600 hover:bg-emerald-500 rounded-xl font-semibold transition-all shadow-lg flex items-center gap-2">
                    <BanknotesIcon className="w-5 h-5" />
                    Withdraw Funds
                  </button>
                  <button className="px-6 py-3 bg-slate-800 hover:bg-slate-700 rounded-xl font-semibold transition-all flex items-center gap-2">
                    <DocumentArrowDownIcon className="w-5 h-5" />
                    Download Statement
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-6">
            {[
              { id: 'all', label: 'All Transactions' },
              { id: 'credit', label: 'Credits' },
              { id: 'debit', label: 'Debits' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setFilter(tab.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  filter === tab.id
                    ? 'bg-emerald-600 text-white shadow-lg'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Transactions */}
          <div>
            <h3 className="text-lg font-bold text-slate-900 mb-4">Recent Transactions</h3>
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-emerald-600 border-r-transparent"></div>
                  <p className="mt-4 text-slate-600">Loading transactions...</p>
                </div>
              </div>
            ) : filteredTransactions.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-16 text-center">
                <WalletIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 font-medium">No transactions found</p>
                <p className="text-slate-400 text-sm mt-1">Transactions will appear here when you receive payments</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm divide-y divide-slate-100">
                {filteredTransactions.map(tx => (
                  <div key={tx._id} className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        ['credit', 'deposit'].includes(tx.type)
                          ? 'bg-green-100 text-green-600'
                          : 'bg-red-100 text-red-600'
                      }`}>
                        {['credit', 'deposit'].includes(tx.type) ? (
                          <ArrowDownIcon className="w-6 h-6" />
                        ) : (
                          <ArrowUpIcon className="w-6 h-6" />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{tx.description || tx.type}</p>
                        <p className="text-sm text-slate-500">
                          {new Date(tx.createdAt).toLocaleDateString()} at{' '}
                          {new Date(tx.createdAt).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-bold ${
                        ['credit', 'deposit'].includes(tx.type) ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {['credit', 'deposit'].includes(tx.type) ? '+' : '-'}₦{tx.amount?.toLocaleString()}
                      </p>
                      <p className="text-xs text-slate-500 capitalize">{tx.status || 'completed'}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}

export default CenterWalletPage

